import React, { Fragment } from 'react';
import { withRouter } from 'react-router';
import FormView from '../../FormView';
import { ICustomerDetailsProps } from '../interfaces';
import { useRetrieveCustomerDetails } from 'api/customerEnquiry/details';

const noObjectTabs = ['Notepad', 'SalesDiary', 'DiaryDetails'];
const initializeInitialValues = (selectedTab: string, selectedCustomer: any) => {
  if (!noObjectTabs.includes(selectedTab)) return null;

  return {
    params: {
      CustomerId: (selectedCustomer?.CustomerId) || parseInt(new URLSearchParams(location.search).get('CustomerId') || '0'),
    }
  };
};

const initializeInitialSchema = (selectedTab: string) => (noObjectTabs.includes(selectedTab) ? {} : null);

const CustomerDetail: React.FC<ICustomerDetailsProps> = ({
  path,
  onInitialLoad,
  selectedTab,
  selectedForm,
  operationMode,
  formName,
  isBrowseLookUpOpen,
  isV2,
  selectedCustomer,
  selectedContact,
  selectedContactSchema,
  selectedAddress,
  selectedAddressSchema,
  selectedProduct,
  selectedProductSchema,
  selectedMarketing,
  selectedMarketingSchema,
}) => {
  const [initialValues, setInitialValues] = React.useState<any>(initializeInitialValues(selectedTab, selectedCustomer));
  const [valuesSchema, setValuesSchema] = React.useState<any>(initializeInitialSchema(selectedTab));

  const fetchCustomerDetailsMutation = useRetrieveCustomerDetails();

  React.useEffect(
    () => {
      if (selectedCustomer?.CustomerId) fetchInitialValues(selectedCustomer?.CustomerId);
    },
    [selectedTab, selectedCustomer, selectedContact, selectedAddress, selectedProduct, selectedMarketing]
  );
  React.useEffect(
    () => {
      if (path) {
        switch (path) {
          case '/customer-enquiry/customer-details':
            onInitialLoad('CustomerDetails');
            break;
          case '/customer-enquiry/customer-details/contacts':
            onInitialLoad('Contacts');
            break;
          case '/customer-enquiry/customer-details/delivery-details':
            onInitialLoad('DeliveryDetails');
            break;
          case '/customer-enquiry/customer-details/products':
            onInitialLoad('CustomerProducts');
            break;
          case '/customer-enquiry/customer-details/marketing':
            onInitialLoad('Marketing');
            break;
          case '/customer-enquiry/customer-details/notepad':
            onInitialLoad('Notepad');
            break;
          case '/customer-enquiry/customer-details/sales-diary':
            onInitialLoad('SalesDiary');
            break;
          case '/customer-enquiry/customer-details/sales-diary/diary-details':
            onInitialLoad('DiaryDetails');
            break;
          case '/customer-enquiry/customer-details/user-fields':
            onInitialLoad('UserFields');
            break;
          default:
            onInitialLoad('CustomerDetails');
        }
      }
    },
    [path, onInitialLoad]
  );

  const getDynamicBrowseLookup = (tab: string) => {
    if (selectedCustomer) {
      const { CustomerId } = selectedCustomer;
      switch (tab) {
        case 'Contacts':
          return {
            variant: 'Contacts',
            searchParams: { CustomerId },
            searchPlaceholder: 'Search for contacts...',
            isBrowseLookUp: true,
            useLookupPanel: false,
          };
        case 'Products':
        case 'CustomerProducts':
          return {
            variant: tab,
            searchParams: { CustomerId },
            isBrowseLookUp: true,
            searchPlaceholder: 'Search for products...',
            useLookupPanel: false,
          };
        case 'Marketing':
          return {
            variant: 'Marketing',
            searchParams: { CustomerId },
            isBrowseLookUp: true,
            searchPlaceholder: 'Search for attributes...',
            useLookupPanel: false,
          };
        case 'DeliveryDetails':
          return {
            variant: 'DeliveryDetails',
            searchParams: { CustomerId },
            searchPlaceholder: 'Search for addresses...',
            isBrowseLookUp: true,
            useLookupPanel: false,
          };
        default:
          return null;
      }
    }

    return null;
  };

  const fetchInitialValues = async (CustomerId: string | undefined) => {

    switch (selectedTab) {
      case 'CustomerDetails': {
        const { CustomerDetails } = await fetchCustomerDetailsMutation.mutateAsync({
          CustomerId: parseInt(CustomerId)
        });
        setInitialValues(CustomerDetails?.inlineObject);
        setValuesSchema(CustomerDetails?.schema);
        break;
      }
      case 'Contacts':
        setInitialValues(selectedContact);
        setValuesSchema(selectedContactSchema);
        break;
      case 'DeliveryDetails':
        setInitialValues(selectedAddress);
        setValuesSchema(selectedAddressSchema);
        break;
      case 'CustomerProducts':
        setInitialValues(selectedProduct);
        setValuesSchema(selectedProductSchema);
        break;
      case 'Marketing':
        setInitialValues(selectedMarketing);
        setValuesSchema(selectedMarketingSchema);
        break;
      case 'Notepad':
      case 'SalesDiary':
      case 'DiaryDetails':
        setInitialValues(initializeInitialValues(selectedTab, selectedCustomer));
        setValuesSchema({});
        break;
      default:
        setInitialValues(null);
        setValuesSchema(null);
    }
  };

  return (
    selectedForm && (
      <Fragment>
        <FormView
          formName={(selectedTab === 'SalesDiary' && 'Diary') || formName}
          browseLookUpOpen={isBrowseLookUpOpen}
          browseLookUpComponent={getDynamicBrowseLookup(selectedTab)}
          includeTabsHeight={false}
          schema={selectedForm}
          initialValues={initialValues}
          operationMode={operationMode}
          valuesSchema={valuesSchema}
          fieldExtensions={{
            PhoneMobile: {
              visible: selectedTab === 'DeliveryDetails' && !isV2,
            },
          }}
        />
      </Fragment>
    )
  );
};

export default withRouter(CustomerDetail);
